import { GetStaticProps } from 'next'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { Seo } from '@/layout/Seo'
import { NextPageWithLayout } from '@/layouts'
import { getWebClient } from '@/services/ApolloClient'
import { getProjectsByPhase } from '@/services/ProjectsService/ProjectsService'
import { getTorchContent, getTorches } from '@/services/TorchesService'
import { ICampaignAssets } from '@/types/generated-contentful'
import { getGenericStaticPaths } from '@/utils/GetGenericStaticPaths'
import { getLocaleFromParams } from '@/utils/LocaleUtil'
import { getStaticPropsErrorHandler, getUrlFromThisFilePath } from '@/utils/nextUtils/nextErrorHandlers'
import { redact } from '@/utils/sensitive-data'
import { getBasePath } from '@/utils/sitemap/base'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { TorchesView, TorchesViewProps } from '@/views/TorchesView'

interface TorchPageProps extends TorchesViewProps {
  locale: string
}

export const TorchPage: NextPageWithLayout<TorchPageProps> = (props) => {
  const { t } = useTranslate('torches')

  const basePath = getBasePath(props.locale)
  const url = `${basePath}${paths.torches.index}`
  const title = t('angelStudiosTorchMetaTitle', 'Torches | Angel Studios')
  const description = t(
    'welcomeDescription',
    'Potential upcoming movie or TV series that amplify light. Express Interest on the content you believe will brighten up our world.',
  )

  return (
    <>
      <Seo
        title={title}
        description={description}
        canonical={url}
        openGraph={{
          title,
          description,
          images: [
            {
              url: 'https://images.angelstudios.com/image/upload/c_fill,h_630,w_1200/v1706716716/angel-studios/Show_Wall_Grid-240117-01.jpg',
              width: 1200,
              height: 630,
              alt: 'Torches',
            },
          ],
          url,
        }}
        path={paths.torches.index}
      />
      <TorchesView {...props} />
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({ params }) =>
  getStaticPropsErrorHandler({ requestUrl: getUrlFromThisFilePath(params) }, async () => {
    const locale = getLocaleFromParams(params)
    const torchesData = await getTorches()

    const campaignSlugs = new Set<string>()
    torchesData?.torchesInvesting?.forEach((torch) => {
      if (torch?.campaignSlug) campaignSlugs.add(torch?.campaignSlug)
    })

    const projectsExpressingInterest = await getProjectsByPhase(
      { phaseType: 'TESTING_THE_WATERS', filter: { active: true } },
      getWebClient(),
    )
    projectsExpressingInterest.forEach((project) => {
      if (project.activeInterestInitiative?.contentfulSlug) {
        campaignSlugs.add(project.activeInterestInitiative.contentfulSlug)
      }
    })

    const campaignAssets = await Promise.all(
      [...campaignSlugs].map((slug) => {
        return getTorchContent<ICampaignAssets>({
          content_type: 'campaignAssets',
          'fields.slug': slug,
        })
      }),
    )

    return {
      props: {
        ...(await loadTranslations(locale, ['common', 'torches', 'account'])),
        heroMeta: torchesData?.torchLandingMeta,
        locale,
        campaignAssets: campaignAssets.map((collection) => collection?.items?.[0]).filter(Boolean),
        projectsExpressingInterest: redact(projectsExpressingInterest, ['phases']),
        torchesInvesting: torchesData?.torchesInvesting,
        torchesExpressingInterest: torchesData?.torchesExpressingInterest,
      },
      revalidate: Durations.FIVE_MINUTES_IN_SECONDS,
    }
  })

export const getStaticPaths = getGenericStaticPaths

export default TorchPage
